import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {setCapitalErrorHandler} from '../../../api/capitalAxios';
import {setWalletErrorHandler} from '../../../api/walletAxios';
import './Toast.scss';

const ToastContext = createContext(null);

let _nextId = 0;
const DURATION = 4500;
const LEAVE_DURATION = 280;

export const ToastProvider = ({children}) => {
    const [toasts, setToasts] = useState([]);

    const dismiss = useCallback((id) => {
        setToasts(t => t.map(x => x.id === id ? {...x, leaving: true} : x));
        setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), LEAVE_DURATION);
    }, []);

    const add = useCallback((type, message) => {
        const id = ++_nextId;
        setToasts(t => [...t, {id, type, message, leaving: false}]);
        setTimeout(() => dismiss(id), DURATION);
        return id;
    }, [dismiss]);

    useEffect(() => {
        const handler = (msg) => add('error', msg);
        setCapitalErrorHandler(handler);
        setWalletErrorHandler(handler);

        const onUnhandledRejection = (e) => {
            const msg = e.reason?.message || String(e.reason) || 'Unexpected error';
            add('error', msg);
        };
        window.addEventListener('unhandledrejection', onUnhandledRejection);

        return () => {
            setCapitalErrorHandler(null);
            setWalletErrorHandler(null);
            window.removeEventListener('unhandledrejection', onUnhandledRejection);
        };
    }, [add]);

    const ctx = {
        error:   (msg) => add('error', msg),
        success: (msg) => add('success', msg),
        info:    (msg) => add('info', msg),
    };

    return (
        <ToastContext.Provider value={ctx}>
            {children}
            <div className="toast-container">
                {toasts.map(t => (
                    <div
                        key={t.id}
                        className={`toast toast--${t.type}${t.leaving ? ' toast--out' : ''}`}
                        onClick={() => dismiss(t.id)}
                        role="alert"
                    >
                        <span className="toast__icon">
                            {t.type === 'error' ? '✕' : t.type === 'success' ? '✓' : 'i'}
                        </span>
                        <span className="toast__message">{t.message}</span>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};

export const useToast = () => useContext(ToastContext);

// ── Error Boundary ────────────────────────────────────────────────────

class ErrorBoundaryInner extends React.Component {
    constructor(props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError() {
        return {hasError: true};
    }

    componentDidCatch(error) {
        this.props.onError(error.message || 'Unexpected error');
    }

    render() {
        if (this.state.hasError) {
            return (
                <p className="capital-page__placeholder">
                    Something went wrong. Try refreshing the page.
                </p>
            );
        }
        return this.props.children;
    }
}

export const ErrorBoundary = ({children}) => {
    const {error} = useToast();
    return <ErrorBoundaryInner onError={error}>{children}</ErrorBoundaryInner>;
};
